(() => {
  if (customElements.get('ps-product-feed')) return;

  class PSProductFeed extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected || this.initialized) return;
      this.grid = this.querySelector('[data-feed-grid]');
      this.more = this.querySelector('[data-feed-more]');
      this.status = this.querySelector('[data-feed-status]');
      this.sentinel = this.querySelector('[data-feed-sentinel]');
      if (!this.grid || !this.more || !this.status || !this.sentinel) return;
      this.initialized = true;
      this.lifecycle = (this.lifecycle || 0) + 1;
      this.products = null;
      this.cursor = 0;
      this.autoPages = 0;
      this.grid.replaceChildren();
      this.more.hidden = false;
      this.more.disabled = false;
      this.more.textContent = this.dataset.loadMore;
      this.onClick = () => this.loadMore(true);
      this.more.addEventListener('click', this.onClick);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadMore(false);
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.lifecycle = (this.lifecycle || 0) + 1;
      this.observer?.disconnect();
      this.more?.removeEventListener('click', this.onClick);
      this.loading = false;
      this.initialized = false;
    }

    async loadMore(manual) {
      if (this.loading || !this.isConnected) return;
      if (!manual && (this.failed || this.autoPages >= 3)) return;
      if (this.products && this.cursor >= this.products.length) return;
      const lifecycle = this.lifecycle;
      const firstLoad = !this.products;
      this.loading = true;
      this.failed = false;
      this.observer?.disconnect();
      this.more.disabled = true;
      this.more.textContent = this.dataset.loading;
      this.grid.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loading;
      this.classList.remove('ps-product-feed--error');

      try {
        if (!this.products) {
          const seed = JSON.parse(this.dataset.seed);
          if (!window.PlainSightDiscovery?.load) throw new Error('Product discovery unavailable');
          const { products, engine } = await window.PlainSightDiscovery.load({
            catalogUrl: this.dataset.catalogUrl,
            rulesUrl: this.dataset.rulesUrl
          });
          if (!this.isConnected || lifecycle !== this.lifecycle) return;
          const exclude = [seed.id, seed.handle];
          document.querySelectorAll('[data-paired-product-handle]').forEach((item) => {
            exclude.push(item.dataset.pairedProductHandle);
          });
          const ranked = engine.rank(products, [seed], {
            exclude, mode: 'similar', limit: products.length, allowBroader: true
          });
          const seen = new Set(exclude.map(String));
          this.products = ranked.filter((item) => {
            if (!item.available || !item.image || seen.has(String(item.id)) || seen.has(item.handle)) return false;
            seen.add(String(item.id));
            seen.add(item.handle);
            return true;
          });
        }
        if (!this.isConnected || lifecycle !== this.lifecycle) return;
        const batch = this.products.slice(this.cursor, this.cursor + 12);
        const fragment = document.createDocumentFragment();
        let firstCard;
        batch.forEach((product) => {
          const card = this.createCard(product);
          firstCard ||= card;
          fragment.append(card);
        });
        this.grid.append(fragment);
        this.cursor += batch.length;
        this.autoPages = manual ? 0 : this.autoPages + (firstLoad ? 0 : 1);
        const finished = this.cursor >= this.products.length;
        this.more.hidden = finished;
        this.more.textContent = this.dataset.loadMore;
        const announcement = this.dataset.productsLoaded.replace('[count]', String(batch.length));
        this.status.textContent = finished
          ? this.dataset.end
          : this.autoPages >= 3 ? `${announcement} ${this.dataset.autoPaused}` : announcement;
        if (!this.products.length) this.hidden = true;
        if (manual) firstCard?.querySelector('a')?.focus({ preventScroll: true });
      } catch (error) {
        if (!this.isConnected || lifecycle !== this.lifecycle) return;
        this.failed = true;
        this.classList.add('ps-product-feed--error');
        this.status.textContent = this.dataset.error;
        this.more.textContent = this.dataset.retry;
      } finally {
        if (this.isConnected && lifecycle === this.lifecycle) {
          this.loading = false;
          this.grid.removeAttribute('aria-busy');
          this.more.disabled = false;
          if (!this.failed && !this.more.hidden && this.autoPages < 3) this.observer?.observe(this.sentinel);
        }
      }
    }

    element(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    }

    createCard(product) {
      const item = this.element('li', 'ps-product-feed__item');
      item.dataset.productId = String(product.id);
      const article = this.element('article', 'ps-collection-card');
      const media = this.element('a', 'ps-collection-card__media');
      media.href = product.url;
      media.setAttribute('aria-label', product.title);
      const image = this.element('img');
      image.alt = product.imageAlt || product.title;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 600;
      image.height = 600;
      image.sizes = '(min-width: 1280px) 283px, (min-width: 990px) calc((100vw - 148px) / 4), (min-width: 750px) calc((100vw - 92px) / 2), calc((100vw - 52px) / 2)';
      try {
        const imageUrl = new URL(product.image, window.location.origin);
        image.srcset = [240, 360, 480, 600].map((width) => {
          imageUrl.searchParams.set('width', String(width));
          return `${imageUrl.href} ${width}w`;
        }).join(', ');
      } catch (error) {}
      image.src = product.image;
      media.append(image);

      const heading = this.element('h3', 'ps-collection-card__title');
      const title = this.element('a', '', product.title);
      title.href = product.url;
      heading.append(title);
      const price = this.element('div', 'ps-collection-card__price ps-product-feed__price');
      price.append(this.element('span', '', product.priceText));
      if (product.compareText) price.append(this.element('s', '', product.compareText));

      const reviews = this.element('div', 'ps-collection-card__reviews');
      const reviewCount = Number(product.reviewCount);
      const reviewRating = Number(product.reviewRating);
      if (reviewCount > 0 && reviewRating > 0 && reviewRating <= 5) {
        const rating = reviewRating.toFixed(1);
        const link = this.element('a');
        link.href = `${product.url.split('#')[0]}#looxReviewsFrame`;
        link.setAttribute('aria-label', this.dataset.ratingLabel.replace('[rating]', rating).replace('[count]', String(reviewCount)));
        const star = this.element('span', 'ps-collection-card__star', '★');
        star.setAttribute('aria-hidden', 'true');
        const countTemplate = reviewCount === 1 ? this.dataset.reviewOne : this.dataset.reviewMany;
        link.append(star, this.element('span', '', rating), this.element('span', 'ps-collection-card__count', countTemplate.replace('[count]', String(reviewCount))));
        reviews.append(link);
      }
      article.append(media, heading, price, reviews);
      item.append(article);
      return item;
    }
  }

  customElements.define('ps-product-feed', PSProductFeed);
})();
